import { useState } from 'react';
import { RiTimeLine, RiArmchairLine } from '@remixicon/react';
import type { LiveSearchDeparture } from '../../services/liveSearchService';
import type { VelocityReading } from '../../services/velocityTracker';
import { localizePlace } from '../../i18n/places';
import { cn } from '../../lib/utils';

interface Props {
  departure: LiveSearchDeparture;
  velocity?: VelocityReading | null;
}

export function DepartureTicket({ departure: d, velocity }: Props) {
  const [stopsExpanded, setStopsExpanded] = useState(false);

  const isCancelled = d.status === 'annule';
  const isDeparted = d.status === 'assure';
  const isFull = d.seatsAvailable === 0 && !isDeparted && !isCancelled;
  const isClosed = isCancelled || isDeparted || isFull;

  const [depH, depM] = d.time.split(':').map(Number);
  const depMin = depH * 60 + depM;
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const minutesLeft = depMin - nowMin;
  const isUrgent = !isClosed && !d.isPast && minutesLeft >= 0 && minutesLeft <= 15;

  const statusLabel = isCancelled ? 'Annulé'
    : isDeparted ? 'Parti'
    : isFull ? 'Complet'
    : isUrgent ? `Dans ${minutesLeft} min`
    : 'Ouvert';

  const statusStyle = isCancelled || isDeparted ? 'bg-surface-ui text-ink-faint'
    : isFull ? 'bg-warn-muted text-warn-dark'
    : isUrgent ? 'bg-warn-muted text-warn-dark'
    : 'bg-brand-muted text-brand-status';

  const availablePct = d.seatsTotal > 0 ? (d.seatsAvailable / d.seatsTotal) * 100 : 100;
  const seatColor = isClosed ? 'text-ink-faint'
    : availablePct < 10 ? 'text-danger'
    : availablePct < 40 ? 'text-warn-dark'
    : 'text-brand';

  const destLabel = localizePlace(d.destination, 'fr');
  // For intermediate stops: destination = searched city, headOfLine = terminal
  // For direct: headOfLine = destination or same city
  const viaLabel = d.headOfLine && d.headOfLine !== d.destination
    ? localizePlace(d.headOfLine, 'fr')
    : null;

  return (
    <div className={cn(
      'relative bg-white rounded-2xl overflow-visible shadow-sm border border-gray-100 transition-opacity',
      d.isPast && 'opacity-50',
    )}>

      <div className="px-5 pb-3 pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <p className="font-data font-bold text-[30px] text-ink leading-none tracking-tight" dir="ltr">
              {d.time}
            </p>
            {isUrgent && (
              <span className="flex items-center gap-1 font-display text-[12px] font-semibold text-warn-dark">
                <RiTimeLine className="h-3.5 w-3.5" />
                {statusLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isUrgent && (
              <span className={cn(
                'text-[12px] font-display font-semibold px-2.5 py-1 rounded-full',
                statusStyle,
              )}>
                {statusLabel}
              </span>
            )}
            {d.priceDA !== null && (
              <span className="font-data font-bold text-[13px] text-ink-muted">
                {d.priceDA.toLocaleString('fr-DZ')} <span className="font-display font-normal text-[11px]">DZD</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="shrink-0 min-w-[52px]">
            <p className="font-display font-bold text-[14px] text-ink leading-tight">Alger</p>
            <p className="font-display text-[11px] text-ink-faint">Kharrouba</p>
          </div>

          <img src="/new_assets/icons/flight-takeoff-line.svg" className="h-4 w-4 shrink-0" aria-hidden />
          <div className="flex-1 border-t border-dashed border-line-light" />
          <img src="/new_assets/icons/material-symbols_flight-land.svg" className="h-4 w-4 shrink-0 opacity-40" aria-hidden />

          <div className="shrink-0 text-end min-w-[52px]">
            <p className="font-display font-bold text-[14px] text-ink leading-tight">{destLabel}</p>
            {viaLabel && (
              <p className="font-display text-[11px] text-ink-faint">
                {d.isIntermediateStop ? 'arrêt · ' : '→ '}{viaLabel}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="relative h-px bg-dashed-separator mx-0">
        <div className="absolute inset-x-4 h-px border-t border-dashed border-line-light" />
        <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-surface-page border border-gray-100" />
        <span className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-surface-page border border-gray-100" />
      </div>

      <div className="px-5 pt-3.5 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <RiArmchairLine className={cn('h-5 w-5 shrink-0', isClosed ? 'opacity-30 text-ink-faint' : seatColor)} aria-hidden />
            <div>
              <p className={cn('font-display text-[13px] font-medium leading-tight', seatColor)}>
                {isClosed
                  ? (isCancelled ? 'Voyage annulé'
                    : isDeparted ? 'Bus déjà parti'
                    : 'Complet')
                  : `${d.seatsAvailable} place${d.seatsAvailable > 1 ? 's' : ''} restante${d.seatsAvailable > 1 ? 's' : ''}`
                }
              </p>
              {!isClosed && !d.isPast && (() => {
                let text: string;
                let color: string;
                if (velocity && velocity.level !== 'stalled') {
                  text = velocity.level === 'fast' ? 'Se remplit vite'
                    : velocity.level === 'moving' ? 'Ça bouge'
                    : 'Encore des places';
                  color = velocity.level === 'fast' ? 'text-danger'
                    : velocity.level === 'moving' ? 'text-warn-dark'
                    : 'text-brand';
                } else {
                  text = availablePct < 10 ? 'Presque complet'
                    : availablePct < 40 ? 'Se remplit vite'
                    : 'Encore des places';
                  color = seatColor;
                }
                return <p className={cn('font-display text-[11px] leading-tight', color)}>{text}</p>;
              })()}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {d.transporterName && (
              <p className="font-display text-[12px] text-ink-faint truncate max-w-[110px]">
                {d.transporterName}
              </p>
            )}
            {viaLabel && (
              <button
                onClick={() => setStopsExpanded(!stopsExpanded)}
                className="flex items-center gap-1 text-[12px] font-display font-semibold text-brand"
              >
                <img
                  src={stopsExpanded ? '/new_assets/icons/eye-off-line.svg' : '/new_assets/icons/eye-line.svg'}
                  className="h-3.5 w-3.5"
                  aria-hidden
                />
                {stopsExpanded ? 'Masquer' : 'Afficher'}
              </button>
            )}
          </div>
        </div>

        {stopsExpanded && viaLabel && (
          <div className="mt-3 pt-3 border-t border-dashed border-line-light space-y-2">
            <StopRow city="Alger" sub="Kharrouba" isOrigin />
            <StopRow city={destLabel} />
            <StopRow city={viaLabel} isLast />
          </div>
        )}
      </div>
    </div>
  );
}

function StopRow({
  city, sub, isOrigin = false, isLast = false,
}: {
  city: string;
  sub?: string;
  isOrigin?: boolean;
  isLast?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex flex-col items-center shrink-0 w-4">
        <div className={cn(
          'w-3 h-3 rounded-full border-2',
          isOrigin || isLast ? 'border-brand bg-white' : 'border-line bg-line',
        )} />
        {!isLast && <div className="w-px h-5 bg-line-light mt-0.5" />}
      </div>
      <div className="pb-3">
        <p className="font-display font-semibold text-[13px] text-ink leading-tight">{city}</p>
        {sub && <p className="font-display text-[11px] text-ink-faint">{sub}</p>}
      </div>
    </div>
  );
}
